import type { CorpusItem } from './corpus';
import { feedsInFlight, isGathered } from './gathering';
import type { Gathering } from './gathering';

/**
 * Which thoughts are on the wall: the view and the horizon.
 *
 * The views are about removing noise rather than finding something, and one
 * of them removes almost nothing — 4,851 of 4,897 captures are cited by no
 * arc. That is the honest shape of the pile, and it is why the horizon exists
 * beside it: the default is a fortnight, and the whole corpus is a deliberate
 * act rather than the thing a page opens with.
 */

export const VIEWS = ['everything', 'ungathered', 'inFlight'] as const;
export type View = (typeof VIEWS)[number];

export const VIEW_LABELS: Record<View, string> = {
  everything: 'everything',
  ungathered: 'nothing gathered yet',
  inFlight: 'in something in flight',
};

export const HORIZONS = ['2 weeks', '90 days', 'all of it'] as const;
export type Horizon = (typeof HORIZONS)[number];

const DAYS_IN: Record<Horizon, number> = { '2 weeks': 14, '90 days': 90, 'all of it': Infinity };

export function inView(item: CorpusItem, view: View, gathering: Gathering): boolean {
  const entries = gathering.get(item.path);
  if (view === 'ungathered') return !isGathered(entries);
  if (view === 'inFlight') return feedsInFlight(entries);
  return true;
}

export function inHorizon(item: CorpusItem, horizon: Horizon, now: Date): boolean {
  if (horizon === 'all of it') return true;
  if (!item.at) return false;
  return (now.getTime() - item.at.getTime()) / 86_400_000 <= DAYS_IN[horizon];
}
